import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { formValueSelector } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import { Button } from 'reactstrap';
import { ITINERARY_FOR_SCHEDULING_PATH } from '../../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../../common/resource/proptypes/CommonPropTypes';
import ItinerarySearchForm from '../ItinerarySearchForm';
import { INT_DATE_FORMAT } from '../../../../../config/locale';
import {
  searchItineraryBetweenDates,
  clearItineraries,
  putAssignBusses,
  postItinerarySuggestions,
  clearItinerarySuggestions,
  postExportItineraryList,
  getExportRegisteredBusFleet,
} from '../../../../../actions';
import { DEFAULT_QUERY_GET_ALL } from '../../../../../config/queries';
import {
  BUSES_WITHOUT_TRIP_CIRCUIT_STATUS_ERROR_MESSAGE,
  DATA_NOT_REGISTERED_ERROR_MESSAGE,
} from '../../../../../config/messages';
import Content from '../../../../layout/Content';
import { tzNormalizeDate } from '../../../../../utils/date';
import ItinerariesForSchedulingTable from './ItinerariesForSchedulingTable';
import { hadResponseSuccessWithErrorCode } from '../../../../../utils/error-handlers';
import { orderBy } from '../../../../../utils/array';
import MultiSelectCheck from '../../../../common/forms/select/MultiSelectCheck';
import {
  ITINERARY_SCHEDULING_COLUMN_OPTIONS,
  ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS,
  MAX_PAGE_SIZE,
} from '../../../../../config/constants';

const selector = formValueSelector('ItinerarySearchForm');

const ITINERARY_SCHEDULING_ALL_COLUMN_IDS =
  ITINERARY_SCHEDULING_COLUMN_OPTIONS.map((opt) => opt.value);

const ItinerariesForScheduling = ({
  dispatchPutAssignBusses,
  dispatchClearItineraries,
  dispatchSearchItineraryBetweenDates,
  breadcrumbs,
  itineraryList,
  fromDateStringValue,
  toDateStringValue,
  dispatchPostItinerarySuggestions,
  dispatchClearItinerarySuggestions,
  dispatchPostExportItineraryList,
  dispatchGetExportRegisteredBusFleet,
  exportLoading,
}) => {
  const [searchValues, setSearchValues] = useState({
    sourceLocationId: 1,
    destinationLocationId: 1,
    fromDateString: tzNormalizeDate({ format: INT_DATE_FORMAT }),
    toDateString: tzNormalizeDate({ format: INT_DATE_FORMAT }),
    activeOnly: true,
    ...DEFAULT_QUERY_GET_ALL,
  });

  const selectedDataRef = useRef([]);

  const [visibleItineraries, setVisibleItineraries] = useState([]);
  const [isFilteredData, setIsFilteredData] = useState(false);
  const [searchKey, setSearchKey] = useState(0);

  const [visibleColumnIds, setVisibleColumnIds] = useState(() => {
    if (typeof window === 'undefined')
      return ITINERARY_SCHEDULING_ALL_COLUMN_IDS;

    try {
      const used = localStorage.getItem(
        ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.USED,
      );

      if (!used) {
        return ITINERARY_SCHEDULING_ALL_COLUMN_IDS;
      }

      const stored = localStorage.getItem(
        ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.VISIBLE,
      );
      if (!stored) return ITINERARY_SCHEDULING_ALL_COLUMN_IDS;

      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id) =>
          ITINERARY_SCHEDULING_ALL_COLUMN_IDS.includes(id),
        );
      }

      return ITINERARY_SCHEDULING_ALL_COLUMN_IDS;
    } catch (e) {
      return ITINERARY_SCHEDULING_ALL_COLUMN_IDS;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const used = localStorage.getItem(
        ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.USED,
      );
      if (!used) {
        localStorage.setItem(
          ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.USED,
          'true',
        );
        localStorage.setItem(
          ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.VISIBLE,
          JSON.stringify(ITINERARY_SCHEDULING_ALL_COLUMN_IDS),
        );
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const handleChangeVisibleColumns = (selectedOptions) => {
    const newIds = selectedOptions.map((opt) => opt.value);
    const finalIds =
      newIds.length > 0 ? newIds : ITINERARY_SCHEDULING_ALL_COLUMN_IDS;

    setVisibleColumnIds(finalIds);

    try {
      localStorage.setItem(
        ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.USED,
        'true',
      );
      localStorage.setItem(
        ITINERARY_SCHEDULING_COLUMNS_STORAGE_KEYS.VISIBLE,
        JSON.stringify(finalIds),
      );
    } catch (e) {
      // ignore
    }
  };

  const searchItinerary = (searchFormValuesx) => {
    dispatchClearItineraries();
    dispatchSearchItineraryBetweenDates(searchFormValuesx);
  };

  const onSubmitToSearch = (formValues) => {
    const values = {
      sourceLocationId: formValues.sourceLocationId
        ? formValues.sourceLocationId.value
        : null,
      destinationLocationId: formValues.destinationLocationId
        ? formValues.destinationLocationId.value
        : null,
      fromDateString: tzNormalizeDate({
        date: formValues.fromDateString,
        format: INT_DATE_FORMAT,
      }),
      toDateString: tzNormalizeDate({
        date: formValues.toDateString,
        format: INT_DATE_FORMAT,
      }),
      size: MAX_PAGE_SIZE,
      directionType: formValues.directionType
        ? formValues.directionType.value
        : null,
      showNorteChico: formValues.showNorteChico || false,
      driverId: formValues.driver ? formValues.driver.value : null,
      cabinCrewId: formValues.cabinCrew ? formValues.cabinCrew.value : null,
      activeOnly: formValues.activeOnly || false,
      onlyCommercial: formValues.onlyCommercial || false,
    };
    if (formValues.serviceType) {
      const serviceTypeList = Array.isArray(formValues.serviceType)
        ? formValues.serviceType
        : [formValues.serviceType];
      if (serviceTypeList.length) {
        values.serviceTypeIds = serviceTypeList.map(
          (serviceType) => serviceType.value,
        );
      }
    }
    setSearchValues(values);
    searchItinerary(values);
    dispatchClearItinerarySuggestions();
    setSearchKey((prev) => prev + 1);
    setIsFilteredData(false);
    setVisibleItineraries([]);
  };

  const validateValues = (formValues) => {
    let valid = true;
    formValues.forEach(({ companyBusId, tripCircuitStatus, cabinCrewList }) => {
      if (!companyBusId) valid = false;
      if (!tripCircuitStatus) valid = false;
      if (!companyBusId && !tripCircuitStatus) valid = true;
      if (!!companyBusId && cabinCrewList) valid = true;
    });
    return valid;
  };

  const onClickBusAssignment = async (formValues) => {
    const valid = validateValues(formValues);

    if (!valid) {
      toastr.error('Error', BUSES_WITHOUT_TRIP_CIRCUIT_STATUS_ERROR_MESSAGE);
      return;
    }

    const getValue = (item) => {
      if (item === null || item === undefined) return null;

      if (typeof item !== 'object') return item;

      if (item.value !== null && item.value !== undefined) {
        return item.value;
      }

      if (item.id !== null && item.id !== undefined) {
        return item.id;
      }

      if (item.cabinCrewId !== null && item.cabinCrewId !== undefined) {
        return item.cabinCrewId;
      }

      return null;
    };

    const registerFormValues = formValues.map(
      ({
        companyBusId,
        tripCircuitStatus,
        id,
        cabinCrewList = [],
        maintenanceReason,
        highlightSuggestion,
        itineraryScheduleFoodRequestList = [],
        busAssignmentScheduleId,
      }) => {
        const foodRequestList = itineraryScheduleFoodRequestList
          .filter(
            (foodRequest) =>
              foodRequest && foodRequest.supplierId && foodRequest.publicType,
          )
          .map((foodRequest) => ({
            supplierId:
              foodRequest.supplierId &&
              foodRequest.supplierId.value !== undefined
                ? foodRequest.supplierId.value
                : foodRequest.supplierId,

            publicType: Array.isArray(foodRequest.publicType)
              ? foodRequest.publicType.map((pt) =>
                  pt && pt.value !== undefined ? pt.value : pt,
                )
              : [
                  foodRequest.publicType &&
                  foodRequest.publicType.value !== undefined
                    ? foodRequest.publicType.value
                    : foodRequest.publicType,
                ],

            foodIds: Array.isArray(foodRequest.foodIds)
              ? foodRequest.foodIds
                  .map((food) =>
                    food && food.value !== undefined ? food.value : food,
                  )
                  .filter((foodId) => foodId !== null && foodId !== undefined)
              : [],
            scheduleDeliveredDate: foodRequest.scheduleDeliveredDate
              ? tzNormalizeDate({
                  date: foodRequest.scheduleDeliveredDate,
                })
              : null,
          }));

        return {
          registeredBusId: getValue(companyBusId),
          tripCircuitStatus: getValue(tripCircuitStatus),
          itineraryId: id,
          cabinCrewIdList: cabinCrewList
            .map((item) => getValue(item))
            .filter((item) => item !== null && item !== undefined),
          maintenanceReason,
          isSuggested: !!highlightSuggestion,
          itineraryScheduleFoodRequestList: foodRequestList,
          busAssignmentScheduleId,
        };
      },
    );

    const response = await dispatchPutAssignBusses(registerFormValues);
    const error = hadResponseSuccessWithErrorCode(response);

    if (error) toastr.error('Error', DATA_NOT_REGISTERED_ERROR_MESSAGE);
    else searchItinerary(searchValues);
  };

  const today = tzNormalizeDate();

  const subtitle = (
    <span>
      Busca los itinerarios que deseas programar, selecciona los buses y
      asígnalos hacliendo click en <strong>Asignar buses</strong>
    </span>
  );

  const getItinineraries = () => {
    if (isFilteredData && visibleItineraries.length > 0)
      return visibleItineraries;
    return itineraryList;
  };

  const handleSuggestion = async () => {
    dispatchClearItinerarySuggestions();

    const fromDateString = tzNormalizeDate({
      date: fromDateStringValue,
      format: INT_DATE_FORMAT,
    });
    const toDateString = tzNormalizeDate({
      date: toDateStringValue,
      format: INT_DATE_FORMAT,
    });

    const newItineraryList = getItinineraries()
      .filter((itinerary) => itinerary.companyBusId === null)
      .filter(
        (itinerary) =>
          !selectedDataRef.current.find(
            (row) => row.id === itinerary.id && !row.highlightSuggestion,
          ),
      )
      .filter((itinerary) => itinerary.active);

    const sortedData = orderBy(newItineraryList, [
      'departuretime',
      'sourceLocationName',
      'destinationLocationName',
    ]);

    const payload = {
      fromDateString,
      toDateString,
      itineraryIdList: sortedData.map((itinerary) => itinerary.id),
    };

    dispatchPostItinerarySuggestions(payload);
  };

  const exportToExcel = () => {
    const newITineraryList = getItinineraries();
    const sortedData = orderBy(newITineraryList, [
      'departuretime',
      'sourceLocationName',
      'destinationLocationName',
    ]);
    dispatchPostExportItineraryList({
      itineraryIdList: sortedData.map((itinerary) => itinerary.id),
    });
  };

  const handleExport = () => {
    const fromDateString = tzNormalizeDate({
      date: fromDateStringValue,
      format: INT_DATE_FORMAT,
    });
    const toDateString = tzNormalizeDate({
      date: toDateStringValue,
      format: INT_DATE_FORMAT,
    });
    dispatchGetExportRegisteredBusFleet({ fromDateString, toDateString });
  };

  const extraButtons = (
    <>
      {itineraryList && itineraryList.length > 0 ? (
        <>
          <Button color="success" className="mr-3" onClick={exportToExcel}>
            <i className="fa fa-file-excel-o" /> Exportar
          </Button>
          <Button
            color="info"
            outline
            className="mr-3"
            onClick={handleSuggestion}
          >
            <i className="fa fa-lightbulb-o" /> Sugerencias de Asignación
          </Button>
        </>
      ) : null}
      <div className="mr-3">
        <MultiSelectCheck
          options={ITINERARY_SCHEDULING_COLUMN_OPTIONS}
          onChange={handleChangeVisibleColumns}
          placeholder="Seleccione columnas..."
          customDisplayText={(selected) => {
            const total = ITINERARY_SCHEDULING_COLUMN_OPTIONS.length;
            if (selected.length === total) return 'Todas las columnas visibles';
            if (!selected.length) return 'Sin columnas seleccionadas';
            return `${selected.length} columnas visibles`;
          }}
          showTags={false}
          minWidth="20rem"
          isClearable={false}
          defaultValue={ITINERARY_SCHEDULING_COLUMN_OPTIONS.filter((opt) =>
            visibleColumnIds.includes(opt.value),
          )}
        />
      </div>
      <Button
        color="success"
        onClick={handleExport}
        className="mr-2"
        disabled={exportLoading}
      >
        <i className="fa fa-file-excel-o" /> Exportar informe
      </Button>
    </>
  );

  const content = (
    <>
      <ItinerarySearchForm
        onSubmit={onSubmitToSearch}
        initialValues={{
          fromDateString: today,
          toDateString: today,
          activeOnly: true,
          onlyCommercial: true,
        }}
        sourceLocationIsRequired={false}
        destinationLocationIsRequired={false}
        searchByDateRange
        extraButtons={extraButtons}
        filterServiceType
        multipleServiceTypes
        showDirectionType
        showNorteChicoField
        showDriverField
        showCabinCrewField
        showActiveOnlyField
        showOnlyCommercialField
        isClearable
      />
      <ItinerariesForSchedulingTable
        onHandleBusAssignment={onClickBusAssignment}
        selectedDataRef={selectedDataRef}
        setVisibleItineraries={setVisibleItineraries}
        setIsFilteredData={setIsFilteredData}
        visibleColumnIds={visibleColumnIds}
        tableKey={searchKey}
      />
    </>
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Itinerarios para Programar"
      subtitle={subtitle}
      content={content}
    />
  );
};

const mapStateToProps = (state) => ({
  breadcrumbs: [
    ...state.ItineraryUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Itinerarios para Programar',
      href: ITINERARY_FOR_SCHEDULING_PATH,
    },
  ],
  loading: state.ItineraryUnit.Itinerary.getIn(['all', 'loading']),
  itineraryList: state.ItineraryUnit.Itinerary.getIn(['all', 'content']).toJS()
    .content,
  fromDateStringValue: selector(state, 'fromDateString'),
  toDateStringValue: selector(state, 'toDateString'),
  exportLoading: state.MechanicalMaintenanceUnit.MaintenanceBusFleet.getIn([
    'all',
    'loading',
  ]),
});

ItinerariesForScheduling.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchSearchItineraryBetweenDates: PropTypes.func.isRequired,
  dispatchClearItineraries: PropTypes.func.isRequired,
  dispatchPutAssignBusses: PropTypes.func.isRequired,
  itineraryList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
  fromDateStringValue: PropTypes.string,
  toDateStringValue: PropTypes.string,
  dispatchPostItinerarySuggestions: PropTypes.func.isRequired,
  dispatchClearItinerarySuggestions: PropTypes.func.isRequired,
  dispatchGetExportRegisteredBusFleet: PropTypes.func.isRequired,
  dispatchPostExportItineraryList: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool.isRequired,
};

ItinerariesForScheduling.defaultProps = {
  fromDateStringValue: null,
  toDateStringValue: null,
};

const mapDispatchToProps = {
  dispatchSearchItineraryBetweenDates: searchItineraryBetweenDates,
  dispatchClearItineraries: clearItineraries,
  dispatchPutAssignBusses: putAssignBusses,
  dispatchPostItinerarySuggestions: postItinerarySuggestions,
  dispatchClearItinerarySuggestions: clearItinerarySuggestions,
  dispatchPostExportItineraryList: postExportItineraryList,
  dispatchGetExportRegisteredBusFleet: getExportRegisteredBusFleet,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItinerariesForScheduling);
